import React, { useState, useCallback, useEffect, useMemo, useRef } from "react"
import { debounce } from "lodash"
import {
  FormControl,
  Grid,
  Autocomplete,
  TextField,
  InputLabel,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material"
import MuiModal from "../shared/mui_modal"

import { abmUsers as ajaxClient } from "../../ajax_clients"

const LOADING_MORE_ID = "__loading_more__"
const SCROLL_FOR_MORE_ID = "__scroll_for_more__"

function UserLabel({ attributes }) {
  const { email, locationName, status, licenses } = attributes
  return (
    <Box>
      <Typography variant="body2">{email}</Typography>
      <Typography variant="caption" color="text.secondary">
        {locationName} · {status} · {licenses} license
        {licenses !== 1 ? "s" : ""}
      </Typography>
    </Box>
  )
}

export default function RetireUsersModal({ showModal, tokens, onDone }) {
  const [selectedUsers, setSelectedUsers] = useState([])
  const [inputValue, setInputValue] = useState("")
  const [userOptions, setUserOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [currentPage, setCurrentPage] = useState(0)
  const [currentSearch, setCurrentSearch] = useState("")
  const listboxRef = useRef(null)
  const scrollPositionRef = useRef(0)

  const tokenIds = useMemo(() => {
    return tokens
      .filter(token => !token.expired && !token.rejected)
      .map(token => token.id)
  }, [tokens])

  const fetchUserOptions = useCallback(
    (searchValue, page = 0, append = false) => {
      setLoading(true)
      const params = {}
      if (page > 0) params.page = page
      if (searchValue) params.search = searchValue
      if (tokenIds.length > 0) params.token_ids = tokenIds.join(",")
      ajaxClient.read(params).then(response => {
        const options = response.data
        const { page: currentPage, pages } = response.meta.pagination

        setUserOptions(prev => (append ? [...prev, ...options] : options))
        setHasMore(currentPage + 1 < pages)
        setCurrentPage(currentPage)
        setCurrentSearch(searchValue)
        setLoading(false)
      })
    },
    [tokenIds]
  )

  const debouncedFetchUserOptions = useMemo(
    () => debounce(searchValue => fetchUserOptions(searchValue), 300),
    [fetchUserOptions]
  )

  useEffect(() => {
    return () => {
      debouncedFetchUserOptions.cancel()
    }
  }, [debouncedFetchUserOptions])

  useEffect(() => {
    if (showModal) {
      fetchUserOptions("")
    }
  }, [showModal, fetchUserOptions])

  const onClose = useCallback(() => {
    setSelectedUsers([])
    setInputValue("")
    setUserOptions([])
    setSaving(false)
    setLoading(false)
    setHasMore(false)
    setCurrentPage(0)
    setCurrentSearch("")
    onDone()
  }, [onDone])

  const handleListScroll = useCallback(
    event => {
      const listbox = event.currentTarget
      scrollPositionRef.current = listbox.scrollTop
      const nearBottom =
        listbox.scrollTop + listbox.clientHeight >= listbox.scrollHeight - 50
      if (nearBottom && hasMore && !loading) {
        fetchUserOptions(currentSearch, currentPage + 1, true)
      }
    },
    [hasMore, loading, currentSearch, currentPage, fetchUserOptions]
  )

  const handleCommit = useCallback(() => {
    if (saving) return

    setSaving(true)
    setLoading(true)
    const userIds = selectedUsers.map(user => user.id) || []
    ajaxClient
      .retire(userIds)
      .done(() => {
        window.addFlashAlert(
          "notice",
          "Selected users have been scheduled for retirement."
        )
      })
      .fail(() => {
        window.addFlashAlert("error", "Failed to retire selected users.")
      })
      .always(() => {
        onClose()
      })
  }, [selectedUsers, onClose])

  const handleInputChange = useCallback(
    (_event, newInputValue, reason) => {
      setInputValue(newInputValue)
      if (reason === "input") {
        debouncedFetchUserOptions(newInputValue)
      }
    },
    [debouncedFetchUserOptions]
  )

  const handleUserSelectChange = useCallback((_event, newValue) => {
    setSelectedUsers(
      newValue.filter(
        o => o.id !== LOADING_MORE_ID && o.id !== SCROLL_FOR_MORE_ID
      )
    )
  }, [])

  const displayOptions = useMemo(() => {
    if (loading && userOptions.length > 0) {
      return [...userOptions, { id: LOADING_MORE_ID }]
    }
    if (hasMore && !loading) {
      return [...userOptions, { id: SCROLL_FOR_MORE_ID }]
    }
    return userOptions
  }, [userOptions, loading, hasMore])

  useEffect(() => {
    if (listboxRef.current && scrollPositionRef.current > 0) {
      listboxRef.current.scrollTop = scrollPositionRef.current
    }
  }, [displayOptions])

  if (!showModal) {
    return null
  }

  return (
    <MuiModal
      onClose={onClose}
      onCommit={handleCommit}
      titleText="Retire ABM Users"
      buttonText="Retire ABM Users"
      cancelButtonText="Cancel"
    >
      <Grid
        item
        xs={12}
        container
        alignItems="center"
        spacing={2}
        sx={{ minHeight: "120px" }}
      >
        <Grid item xs={12}>
          This will disassociate the registered Managed Apple Account with any
          app licenses currently assigned. This may uninstall those apps if
          currently installed. The current Managed Apple Account will attempt to
          be re-associated with licenses when assigned apps are pushed. This is
          useful if the Managed Apple Account on the device has been updated and
          apps are failing to install.
        </Grid>

        <Grid item xs={2}>
          <InputLabel id="user-select-label">User(s) to retire</InputLabel>
        </Grid>
        <Grid item xs={9}>
          <FormControl fullWidth>
            <Autocomplete
              multiple
              disabled={saving}
              id="user-select"
              aria-labelledby="user-select-label"
              options={displayOptions}
              getOptionDisabled={option =>
                option.id === LOADING_MORE_ID ||
                option.id === SCROLL_FOR_MORE_ID
              }
              value={selectedUsers}
              inputValue={inputValue}
              getOptionLabel={option => option.attributes?.email || ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={x => x}
              disableCloseOnSelect
              loading={loading}
              onInputChange={handleInputChange}
              onChange={handleUserSelectChange}
              ListboxProps={{ ref: listboxRef, onScroll: handleListScroll }}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props
                if (
                  option.id === LOADING_MORE_ID ||
                  option.id === SCROLL_FOR_MORE_ID
                ) {
                  const text =
                    option.id === LOADING_MORE_ID
                      ? "Loading more users..."
                      : "Scroll for more users..."
                  return (
                    <li key={key} {...optionProps}>
                      <Typography variant="body2" color="text.secondary">
                        {text}
                      </Typography>
                    </li>
                  )
                }
                return (
                  <li key={key} {...optionProps}>
                    <UserLabel attributes={option.attributes} />
                  </li>
                )
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  variant="standard"
                  placeholder="Type to search"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </FormControl>
        </Grid>
      </Grid>
    </MuiModal>
  )
}
